import { useState } from 'react';
import { X, User, Mail, Calendar, Tag } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  useUpdateTicketStatusMutation, 
  useAssignTicketMutation,
  useGetAllUsersQuery 
} from '@/store/api/apiSlice';
import { SupportTicket, TicketStatus, UserRole } from '@/types';

interface SupportTicketDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: SupportTicket | null;
}

export default function SupportTicketDetailsModal({ isOpen, onClose, ticket }: SupportTicketDetailsModalProps) {
  const [status, setStatus] = useState<TicketStatus>(ticket?.status || TicketStatus.OPEN);
  const [adminResponse, setAdminResponse] = useState('');
  const [assignToAdmin, setAssignToAdmin] = useState(ticket?.assignedTo?.id || '');

  const [updateTicketStatus, { isLoading: isUpdating }] = useUpdateTicketStatusMutation();
  const [assignTicket, { isLoading: isAssigning }] = useAssignTicketMutation();
  const { data: users = [] } = useGetAllUsersQuery({ role: UserRole.SUPER_ADMIN });

  const handleUpdateStatus = async () => {
    if (!ticket) return;

    try {
      const payload: any = {
        status,
      };
      
      if (adminResponse) {
        payload.adminResponse = adminResponse;
      }
      
      await updateTicketStatus({
        id: ticket.id,
        data: payload
      }).unwrap();
      
      onClose();
    } catch (error) {
      console.error('Failed to update ticket status:', error);
    }
  };

  const handleAssignTicket = async () => {
    if (!ticket || !assignToAdmin) return;

    try {
      await assignTicket({
        id: ticket.id,
        data: { adminId: assignToAdmin }
      }).unwrap();
      
      // Update status automatically if needed
      if (ticket.status === TicketStatus.OPEN) {
        await updateTicketStatus({
          id: ticket.id,
          data: { status: TicketStatus.IN_PROGRESS }
        }).unwrap();
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to assign ticket:', error);
    }
  };

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.OPEN:
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case TicketStatus.IN_PROGRESS:
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
      case TicketStatus.RESOLVED:
        return 'bg-green-50 text-green-700 border-green-100';
      case TicketStatus.CLOSED:
        return 'bg-gray-50 text-gray-700 border-gray-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'bg-gray-50 text-gray-700 border-gray-100';
      case 'medium':
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'high':
        return 'bg-orange-50 text-orange-700 border-orange-100';
      case 'urgent':
        return 'bg-red-50 text-red-700 border-red-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  if (!ticket) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Support Ticket Details</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {/* Ticket Info */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{ticket.subject}</h3>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`${getStatusColor(ticket.status)} px-3 py-1 rounded-lg font-medium capitalize`}>
                      {ticket.status.replace('_', ' ')}
                    </Badge>
                    <Badge variant="outline" className={`${getPriorityColor(ticket.priority)} px-3 py-1 rounded-lg font-medium capitalize`}>
                      {ticket.priority}
                    </Badge>
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-600 border-indigo-100 px-3 py-1 rounded-lg font-medium capitalize">
                      {ticket.category}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {new Date(ticket.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* User Information */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">User Information</h4>
              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium text-gray-900">
                    {ticket.user?.firstName} {ticket.user?.lastName}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-gray-900">{ticket.user?.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Tag className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Role:</span>
                  <span className="font-medium text-gray-900 capitalize">{ticket.user?.role}</span>
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Message</h4>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.message}</p>
              </div>
            </div>

            {/* Assignment */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Assign To Admin</h4>
              <div className="flex gap-3">
                <select
                  value={assignToAdmin}
                  onChange={(e) => setAssignToAdmin(e.target.value)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value="">Select admin...</option>
                  {users.filter(u => u.role === UserRole.SUPER_ADMIN).map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.firstName} {admin.lastName}
                    </option>
                  ))}
                </select>
                <Button
                  onClick={handleAssignTicket}
                  disabled={!assignToAdmin || isAssigning}
                  className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {isAssigning ? 'Assigning...' : 'Assign'}
                </Button>
              </div>
              {ticket.assignedTo && (
                <p className="text-sm text-gray-600 mt-2">
                  Currently assigned to: <span className="font-medium text-gray-900">
                    {ticket.assignedTo.firstName} {ticket.assignedTo.lastName}
                  </span>
                </p>
              )}
            </div>

            {/* Status Update */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Update Status</h4>
              <div className="space-y-3">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TicketStatus)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value={TicketStatus.OPEN}>Open</option>
                  <option value={TicketStatus.IN_PROGRESS}>In Progress</option>
                  <option value={TicketStatus.RESOLVED}>Resolved</option>
                  <option value={TicketStatus.CLOSED}>Closed</option>
                </select>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Admin Response (Optional)
                  </label>
                  <textarea
                    value={adminResponse}
                    onChange={(e) => setAdminResponse(e.target.value)}
                    placeholder="Add a response or note for the user..."
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Admin Response Display */}
            {ticket.adminResponse && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Previous Admin Response</h4>
                <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.adminResponse}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 px-6 py-2.5 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-sm font-medium"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateStatus}
              disabled={isUpdating}
              className="flex-1 bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white px-6 py-2.5 rounded-xl text-sm font-medium disabled:opacity-50"
            >
              {isUpdating ? 'Updating...' : 'Update Ticket'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
